import type { Column as TableColumn } from "@replo/design-system/components/table/Table";
import type { Product } from "schemas/generated/product";

import * as React from "react";

import {
  getPriceRange,
  getStatusBadgeClasses,
  getStatusIcon,
} from "@editor/components/dashboard/products/utils";

import Tooltip from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import startCase from "lodash-es/startCase";
import { Package } from "lucide-react";

export function buildShopifyProductTableColumns(): TableColumn<Product>[] {
  return [
    {
      key: "product",
      header: "Product",
      width: "400px",
      renderCell: (product) => (
        <div className="flex items-center gap-3 min-w-0">
          {product.featuredMedia?.url ? (
            <img
              src={product.featuredMedia.url}
              alt={product.title}
              className="h-12 w-12 rounded object-cover flex-shrink-0"
            />
          ) : (
            <div className="bg-component text-muted flex h-12 w-12 items-center justify-center rounded flex-shrink-0">
              <Package className="h-6 w-6" />
            </div>
          )}
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {getStatusIcon(product.status)}
            <div className="flex flex-col min-w-0 flex-1">
              <div className="text-default cursor-pointer text-lg font-medium truncate">
                {product.title}
              </div>
              <div className="text-muted text-sm truncate">
                {product.slug ?? "N/A"}
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "price",
      header: "Price",
      width: "120px",
      renderCell: (product) => (
        <div className="text-muted text-sm">
          {getPriceRange(product.variants)}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "120px",
      renderCell: (product) => (
        <div
          className={twMerge(
            "inline-block rounded-full px-2 py-0.5 text-xs font-medium",
            getStatusBadgeClasses(product.status),
          )}
        >
          {startCase(product.status)}
        </div>
      ),
    },
    {
      key: "vendor",
      header: "Vendor",
      width: "150px",
      renderCell: (product) => (
        <div className="text-muted text-sm truncate">
          {product.vendor ?? "N/A"}
        </div>
      ),
    },
    {
      key: "variants",
      header: "Variants",
      width: "120px",
      renderCell: (product) => {
        const variantCount = product.variants?.length ?? 0;
        const variantTitles =
          product.variants?.map(
            (variant) => variant.title ?? "Untitled variant",
          ) ?? [];

        const visibleTitles = variantTitles.slice(0, 5);
        const remaining = variantTitles.length - 5;
        const tooltipContent =
          remaining > 0
            ? `${visibleTitles.join(", ")}... and ${remaining} more`
            : visibleTitles.join(", ");

        return (
          <Tooltip content={tooltipContent} triggerAsChild side="top">
            <span className="text-muted text-sm cursor-pointer hover:text-foreground">
              {variantCount} {variantCount === 1 ? "variant" : "variants"}
            </span>
          </Tooltip>
        );
      },
    },
    {
      key: "options",
      header: "Options",
      width: "120px",
      renderCell: (product) => {
        const optionCount = product.options?.length ?? 0;

        if (optionCount === 0) {
          return (
            <Tooltip content="No options" triggerAsChild side="top">
              <span className="text-muted text-sm cursor-pointer hover:text-foreground">
                0 options
              </span>
            </Tooltip>
          );
        }

        const optionsList =
          product.options
            ?.map((option) => {
              const values =
                option.optionValues?.map((ov) => ov.value).join(", ") ??
                "No values";
              return `${option.title}: ${values}`;
            })
            .join(", ") ?? "";

        return (
          <Tooltip content={optionsList} triggerAsChild side="top">
            <span className="text-muted text-sm cursor-pointer hover:text-foreground">
              {optionCount} {optionCount === 1 ? "option" : "options"}
            </span>
          </Tooltip>
        );
      },
    },
  ];
}
